import { SiteFooter } from "@components/layout/SiteFooter";
import { SiteHeader } from "@components/layout/SiteHeader";
import { SITE_URL } from "@/lib/site";
import { useTranslations } from "@services/strings";
import Head from "next/head";
import { useRouter } from "next/router";
import { ReactNode } from "react";

/**
 * Page shell: document head (title, description, canonical, hreflang,
 * Open Graph), skip link, header, main and footer.
 */
export const Page = ({
  title,
  description,
  image,
  children,
  headerTone = "white",
}: {
  title: string;
  description: string;
  /** Absolute URL of the picture link previews (WhatsApp, Slack, X) show for this page. */
  image?: string;
  children: ReactNode;
  headerTone?: "white" | "navy";
}) => {
  const { t } = useTranslations();
  const { asPath, locale } = useRouter();
  const path = asPath.split(/[?#]/)[0] ?? "/";
  const canonical = `${SITE_URL}/${locale}${path === "/" ? "" : path}`;

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <link
          rel="alternate"
          hrefLang="ar-EG"
          href={`${SITE_URL}/ar-EG${path === "/" ? "" : path}`}
        />
        <link
          rel="alternate"
          hrefLang="en-EG"
          href={`${SITE_URL}/en-EG${path === "/" ? "" : path}`}
        />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${SITE_URL}${path === "/" ? "" : path}`}
        />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="MNZL" />
        <meta
          property="og:locale"
          content={locale === "ar-EG" ? "ar_EG" : "en_EG"}
        />
        {image ? (
          <>
            <meta property="og:image" content={image} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:image" content={image} />
          </>
        ) : null}
      </Head>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:start-4 focus:top-4 focus:z-50 focus:rounded-full focus:bg-ink focus:px-4 focus:py-2 focus:text-white"
      >
        {t("site.common.skipToContent")}
      </a>
      <SiteHeader tone={headerTone} />
      <main id="main">{children}</main>
      <SiteFooter />
    </>
  );
};
